import { isAxios404 } from './pollJob';
import { getAgentJobStatus, postAgentChatInit } from './agentChatService';
import type { AgentJobStatusResponse, AgentResultPayload } from '@/types/agent.types';

/** Cadencia inicial: 2s los primeros 30s, luego 5s. */
const INITIAL_POLL_INTERVAL_MS = 2_000;
const SLOW_POLL_INTERVAL_MS = 5_000;
const FAST_POLL_WINDOW_MS = 30_000;

// Un solo límite: la respuesta profunda sigue existiendo, solo deja de
// anunciarse de antemano, así que se conserva el techo más alto.
const MAX_WAIT_MS = 5 * 60_000;

const EXPIRED_JOB_MESSAGE =
  'The report has expired or is no longer available. Please generate it again.';

export interface AgentJobPollResult {
  payload: AgentResultPayload;
  raw: AgentJobStatusResponse;
  /** Id de sesión devuelto en el POST de inicio (mismo valor en todos los turnos de la conversación). */
  sessionId: string;
}

function delayAbortable(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      window.clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal?.addEventListener('abort', onAbort);
  });
}

function intervalFor(elapsedMs: number): number {
  return elapsedMs < FAST_POLL_WINDOW_MS ? INITIAL_POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS;
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
}

function throwIfPastDeadline(startedAt: number): void {
  if (Date.now() - startedAt > MAX_WAIT_MS) {
    throw new Error('Maximum wait time exceeded while generating the analysis. Please try again.');
  }
}

function buildResult(res: AgentJobStatusResponse, sessionId: string): AgentJobPollResult {
  if (!res.result_payload) {
    throw new Error('The job completed but returned no data.');
  }
  return {
    payload: res.result_payload as unknown as AgentResultPayload,
    raw: res,
    sessionId,
  };
}

function throwIfJobFailed(res: AgentJobStatusResponse): void {
  if (res.status !== 'FAILED') return;
  throw new Error(res.error_detail || 'The analysis failed on the server.');
}

async function fetchJobStatusForPoll(
  pollUrl: string,
  signal: AbortSignal
): Promise<AgentJobStatusResponse> {
  try {
    return await getAgentJobStatus(pollUrl, signal);
  } catch (err) {
    if (isAxios404(err)) {
      throw new Error(EXPIRED_JOB_MESSAGE);
    }
    throw err;
  }
}

/**
 * Si COMPLETED → resultado; si FAILED → lanza; en otro caso → seguir polling.
 */
function outcomeFromStatusOrContinue(
  res: AgentJobStatusResponse,
  sessionId: string
): AgentJobPollResult | null {
  if (res.status === 'COMPLETED') {
    return buildResult(res, sessionId);
  }
  throwIfJobFailed(res);
  return null;
}

/**
 * Encola el job en el BFF y hace polling hasta COMPLETED/FAILED, con delays
 * cancelables vía `signal`.
 */
export async function runAgentChatWithPolling(
  trimmedQuestion: string,
  signal: AbortSignal,
  sessionId?: string | null
): Promise<AgentJobPollResult> {
  const initRes = await postAgentChatInit(trimmedQuestion, sessionId);
  const pollUrl = initRes.poll_url?.trim();
  const serverSessionId = initRes.session_id?.trim();
  if (!pollUrl) {
    throw new Error('The server did not return a way to follow this request. Please try again.');
  }
  if (!serverSessionId) {
    throw new Error('The server did not return a session for this chat. Please try again.');
  }

  const startedAt = Date.now();

  for (;;) {
    throwIfAborted(signal);
    throwIfPastDeadline(startedAt);

    const statusRes = await fetchJobStatusForPoll(pollUrl, signal);
    const outcome = outcomeFromStatusOrContinue(statusRes, serverSessionId);
    if (outcome !== null) {
      return outcome;
    }

    const elapsed = Date.now() - startedAt;
    await delayAbortable(intervalFor(elapsed), signal);
  }
}
